import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLOR = { pending: "text-orange-600 bg-orange-50", approved: "text-emerald-600 bg-emerald-50", rejected: "text-red-600 bg-red-50", cancelled: "text-slate-500 bg-slate-100" };

export default function EmployeeLeaves() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
  const { data: leaves } = useQuery({ queryKey: ["my-leaves"], queryFn: async () => (await api.get("/leaves/my")).data });

  const submit = async () => {
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
      qc.invalidateQueries({ queryKey: ["my-leaves"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 wp-fade-up">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-head font-extrabold text-slate-900">Leaves</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="request-leave-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Request</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="leave-type-input" placeholder="Leave Type" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">From</label><Input data-testid="leave-from-input" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500">To</label><Input data-testid="leave-to-input" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></div>
              </div>
              <Textarea data-testid="leave-reason-input" placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              <Button data-testid="submit-leave-button" onClick={submit} disabled={!form.from_date || !form.to_date} className="w-full bg-blue-600 hover:bg-blue-700">Submit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {(leaves || []).length === 0 && <p className="text-sm text-slate-400">No leave requests.</p>}
        {(leaves || []).map((l) => (
          <div key={l.id} className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">{l.leave_type}</span>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLOR[l.status]}`}>{l.status}</span>
            </div>
            <div className="text-sm text-slate-500 mt-1">{l.from_date} → {l.to_date} · {l.days} day(s)</div>
            <div className="text-xs text-slate-400 mt-1">{l.reason}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
